import { FC, useEffect, useState } from 'react';
import EHeader from '@/components/everridge/header/EHeader.tsx';
import { cn } from '@/lib/utils.ts';
import useMediaQuery from '@/hooks/useMediaQuery.ts';
import { AppConfig } from '@/config/app.config.ts';
import { useTranslation } from 'react-i18next';
import { Roles, TenantDto } from '@/@types/auth.types.ts';
import HotelCard from '@/features/identity/components/HotelCard.tsx';
import { useTenantsApi } from '@/features/identity/hooks/useTenantsApi.ts';
import { useAuthUser } from '@/features/identity/hooks/useAuthUser.ts';
import { useNavigate } from 'react-router-dom';
import { useDefaultTenantHotel } from '@/features/hotels/hooks/useDefaultTenantHotel.ts';
import { useHotelStorage } from '@/features/hotels/hooks/useHotelStorage.ts';
import EToast from '@/components/everridge/EToast.tsx';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { emptyTenant } from '@/utils/auth.helper';
import ReleaseNews from '../releases/components/ReleaseNews';
import { useMultiCurrencyStorage } from '../hotels/hooks/useHotelMultiCurrencyStorage';

const ChooseHotelPage: FC = () => {
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();
	const [autoSelecting, setAutoSelecting] = useState(false);

	const navigate = useNavigate();
	const { tenants, isTenantsLoading } = useTenantsApi();
	const { setTenant, tenant, user, isAuthenticated } = useAuthUser();
	const { getDefaultHotel } = useDefaultTenantHotel();
	const { setSelectedHotel } = useHotelStorage();
	const userRole = useUserRole();
	const { setMultiCurrency, setOrdersEnabled, setCurrencies } = useMultiCurrencyStorage();

	const handleOpenHotel = async (tenant: TenantDto) => {
		setTenant(tenant);
	};

	useEffect(() => {
		if (userRole === Roles.superAdmin) {
			setTenant({
				id: emptyTenant,
				name: '0000',
				description: '0000',
				logoUrl: '0000',
			});
			navigate('/');
		}
	}, [userRole, navigate, setTenant]);

	useEffect(() => {
		if (
			!isAuthenticated ||
			!user ||
			isTenantsLoading ||
			userRole === Roles.superAdmin ||
			tenants === undefined
		) {
			return;
		}

		// Only treat this as an auth failure when the user genuinely has no tenants to access.
		// Having an unselected tenant (userRole === undefined on this page) is the normal state.
		if (tenants.totalCount > 0) {
			return;
		}

		EToast({
			variant: 'error',
			message: t('auth.loginForm.error.message'),
			description: t('auth.loginForm.error.description'),
		});
		navigate('/auth/login', { replace: true });
	}, [
		isAuthenticated,
		isTenantsLoading,
		navigate,
		t,
		tenants,
		user,
		userRole,
	]);

	useEffect(() => {
		if (tenants?.totalCount === 1) {
			setAutoSelecting(true);
			setTenant(tenants.items[0]);
		}
	}, [tenants]);

	useEffect(() => {
		if (!tenant) return;

		getDefaultHotel()
			.then((hotel) => {
				setAutoSelecting(false);
				if (hotel) {
					setSelectedHotel(hotel);
					setMultiCurrency(hotel.multiCurrency);
					setOrdersEnabled(hotel.ordersEnabled ?? true);
					setCurrencies({
						calendarCurrency: hotel.calendarCurrency,
						financePropertyCurrency: hotel.financePropertyCurrency,
						financeServicesCurrency: hotel.financeServicesCurrency,
						financeOtherCurrency: hotel.financeOtherCurrency,
					});
					navigate('/');
				} else {
					EToast({
						variant: 'error',
						message: 'No default hotel found',
						description:
							'No default hotel found for the selected tenant. Please select a hotel manually.',
					});
				}
			})
			.catch(() => {
				setAutoSelecting(false);
				EToast({
					variant: 'error',
					message: 'Failed to load hotels',
					description:
						'Could not load hotels for the selected tenant. Please try again or contact support.',
				});
			});
	}, [tenant]);

	if (userRole === Roles.superAdmin || isTenantsLoading || autoSelecting) {
		return null;
	}

	return (
		<section className='flex-1 flex flex-col'>
			<EHeader showSidebarTrigger={false} />
			<div className='p-8 flex-1 flex flex-col items-center justify-center gap-6 bg-popover-30'>
				<h4
					className={cn(
						'font-semibold',
						isMobile ? 'text-xl' : 'text-2xl',
					)}>
					{t('auth.chooseHotelForm.text.selectHotel')}
				</h4>
				<div
					className={
						'w-full flex flex-row flex-wrap items-center justify-center gap-4 select-none'
					}>
					{tenants?.items.map((hotel) => (
						<HotelCard
							key={hotel.id}
							icon={hotel.logoUrl}
							title={hotel.name}
							description={hotel.description}
							onClick={() => handleOpenHotel(hotel)}
						/>
					))}
				</div>
			</div>
			<ReleaseNews />
		</section>
	);
};

export default ChooseHotelPage;
